/**
 * Configuration Validation Utilities
 *
 * Validates configuration data before saving.
 * Matches ect2-web behavior: validation runs on save, not on add.
 */

// Validation constants (matching ect2-web)
const MIN_NUMBER_ZONES = 1;
const MIN_NUMBER_COMPONENTS = 1;
const MAX_NUMBER_COMPONENTS = 50;

/**
 * Normalize asset type for comparison
 * @param {string|string[]} assetType - Asset type value
 * @returns {string} Normalized asset type string
 */
const normalizeAssetType = (assetType) => {
	if (!assetType) return '';
	if (Array.isArray(assetType)) {
		return [...assetType].sort().join(',').toLowerCase();
	}
	return String(assetType).toLowerCase();
};

/**
 * Get layout display name
 * @param {Object} layout - Layout object
 * @param {number} index - Layout index (0-based)
 * @returns {string} Display name for the layout
 */
const getLayoutDisplayName = (layout, index) => {
	if (layout.layoutName) return layout.layoutName;
	if (layout.assetType) {
		return Array.isArray(layout.assetType)
			? layout.assetType.join(', ')
			: layout.assetType;
	}
	return `Layout ${index + 1}`;
};

/**
 * Check for duplicate asset types across layouts
 * @param {Array} zoneLayouts - Array of zone layout objects
 * @returns {Array} Array of error objects
 */
export const getDuplicateAssetTypeErrors = (zoneLayouts) => {
	if (!zoneLayouts || zoneLayouts.length === 0) {
		return [];
	}

	const errors = [];
	const assetTypeMap = new Map();

	// Build map of asset types to layout indices
	zoneLayouts.forEach((layout, index) => {
		const normalizedType = normalizeAssetType(layout.assetType);
		if (normalizedType) {
			if (!assetTypeMap.has(normalizedType)) {
				assetTypeMap.set(normalizedType, []);
			}
			assetTypeMap.get(normalizedType).push(index);
		}
	});

	// Find duplicates
	assetTypeMap.forEach((indices, assetType) => {
		if (indices.length > 1) {
			indices.forEach((index) => {
				const layout = zoneLayouts[index];
				errors.push({
					layoutName: getLayoutDisplayName(layout, index),
					layoutIndex: index,
					assetType: layout.assetType,
					errorType: 'DUPLICATE_ASSET_TYPE',
					message: `Duplicate asset type "${assetType}"`,
				});
			});
		}
	});

	return errors;
};

/**
 * Check that each layout has at least one zone
 * @param {Array} zoneLayouts - Array of zone layout objects
 * @returns {Array} Array of error objects
 */
export const getMinZonesErrors = (zoneLayouts) => {
	if (!zoneLayouts || zoneLayouts.length === 0) {
		return [];
	}

	const errors = [];

	zoneLayouts.forEach((layout, index) => {
		const zones = layout.configZones || [];
		if (zones.length < MIN_NUMBER_ZONES) {
			errors.push({
				layoutName: getLayoutDisplayName(layout, index),
				layoutIndex: index,
				errorType: 'LIMIT_NUMBER_OF_ZONES',
				message: `Layout requires at least ${MIN_NUMBER_ZONES} zone`,
			});
		}
	});

	return errors;
};

/**
 * Check that each zone has at least one component (1-50 range)
 * @param {Array} zoneLayouts - Array of zone layout objects
 * @returns {Array} Array of error objects
 */
export const getComponentCountErrors = (zoneLayouts) => {
	if (!zoneLayouts || zoneLayouts.length === 0) {
		return [];
	}

	const errors = [];

	zoneLayouts.forEach((layout, layoutIndex) => {
		const zones = layout.configZones || [];
		const components = layout.components || [];

		zones.forEach((zone, zoneIndex) => {
			const tagComponents = zone.tagComponents || [];
			const zoneName = zone.tag || `Zone ${zoneIndex + 1}`;

			if (tagComponents.length < MIN_NUMBER_COMPONENTS) {
				errors.push({
					layoutName: getLayoutDisplayName(layout, layoutIndex),
					layoutIndex,
					zoneName,
					zoneIndex,
					errorType: 'LIMIT_NUMBER_OF_COMPONENTS',
					message: `Zone "${zoneName}" requires at least ${MIN_NUMBER_COMPONENTS} component`,
				});
			} else if (tagComponents.length > MAX_NUMBER_COMPONENTS) {
				errors.push({
					layoutName: getLayoutDisplayName(layout, layoutIndex),
					layoutIndex,
					zoneName,
					zoneIndex,
					errorType: 'LIMIT_NUMBER_OF_COMPONENTS',
					message: `Zone "${zoneName}" exceeds maximum of ${MAX_NUMBER_COMPONENTS} components`,
				});
			}
		});
	});

	return errors;
};

/**
 * Check that each zone has at least one inspection type assigned
 * @param {Array} zoneLayouts - Array of zone layout objects
 * @returns {Array} Array of error objects
 */
export const getInspectionTypeErrors = (zoneLayouts) => {
	if (!zoneLayouts || zoneLayouts.length === 0) {
		return [];
	}

	const errors = [];

	zoneLayouts.forEach((layout, layoutIndex) => {
		const zones = layout.configZones || [];

		zones.forEach((zone, zoneIndex) => {
			const inspectionTypes = zone.inspectionTypes || [];
			const zoneName = zone.tag || `Zone ${zoneIndex + 1}`;

			if (inspectionTypes.length === 0) {
				errors.push({
					layoutName: getLayoutDisplayName(layout, layoutIndex),
					layoutIndex,
					zoneName,
					zoneIndex,
					errorType: 'MISSING_INSPECTION_TYPES',
					message: `Zone "${zoneName}" requires at least one inspection type`,
				});
			}
		});
	});

	return errors;
};

/**
 * Validate configuration before save
 * @param {Object} config - Configuration object with zoneLayouts and inspectionDetails
 * @returns {Object} Validation result with isValid and errors array
 */
export const validateConfiguration = (config) => {
	const errors = [];

	if (!config) {
		return { isValid: true, errors: [] };
	}

	// Check for duplicate asset types
	const duplicateErrors = getDuplicateAssetTypeErrors(config.zoneLayouts);
	errors.push(...duplicateErrors);

	// Check for minimum zones per layout
	const zoneErrors = getMinZonesErrors(config.zoneLayouts);
	errors.push(...zoneErrors);

	// Check for component count per zone (min 1, max 50)
	const componentErrors = getComponentCountErrors(config.zoneLayouts);
	errors.push(...componentErrors);

	// Check for inspection types assigned to each zone
	const inspectionTypeErrors = getInspectionTypeErrors(config.zoneLayouts);
	errors.push(...inspectionTypeErrors);

	return {
		isValid: errors.length === 0,
		errors,
	};
};

/**
 * Format validation errors for display
 * @param {Array} errors - Array of error objects
 * @returns {Object} Errors grouped by type for display
 */
export const formatValidationErrors = (errors) => {
	const grouped = {};

	errors.forEach((error) => {
		const type = error.errorType || 'UNKNOWN_ERROR';
		if (!grouped[type]) {
			grouped[type] = [];
		}
		grouped[type].push(error);
	});

	return grouped;
};

/**
 * Get user-friendly error title
 * @param {string} errorType - Error type constant
 * @returns {string} User-friendly title
 */
export const getErrorTitle = (errorType) => {
	const titles = {
		DUPLICATE_ASSET_TYPE: 'Duplicate Asset Types',
		LIMIT_NUMBER_OF_ZONES: 'Missing Zones',
		LIMIT_NUMBER_OF_COMPONENTS: 'Component Issues',
		MISSING_INSPECTION_TYPES: 'Missing Inspection Types',
	};
	return titles[errorType] || 'Validation Error';
};

/**
 * Get user-friendly error message
 * @param {string} errorType - Error type constant
 * @param {Array} affectedItems - Array of affected items
 * @returns {string} User-friendly message
 */
export const getErrorMessage = (errorType, affectedItems) => {
	const messages = {
		DUPLICATE_ASSET_TYPE: `The following layouts have duplicate asset types: ${affectedItems.map(e => e.layoutName).join(', ')}. Each asset type must be unique across all layouts.`,
		LIMIT_NUMBER_OF_ZONES: `The following layouts need at least one zone: ${affectedItems.map(e => e.layoutName).join(', ')}.`,
		LIMIT_NUMBER_OF_COMPONENTS: `The following zones have component issues: ${affectedItems.map(e => `${e.layoutName} - ${e.zoneName}`).join(', ')}.`,
		MISSING_INSPECTION_TYPES: `The following zones need inspection types assigned: ${affectedItems.map(e => `${e.layoutName} - ${e.zoneName}`).join(', ')}.`,
	};
	return messages[errorType] || 'Unknown validation error.';
};
